import React from "react";
import { Tab, Tabs } from "react-bootstrap";
import { orderReportSectionViews } from "@core/reports/utils/orderReportSectionViews";

export type ReportSectionTableView = {
  section: {
    type: string;
    label: string;
  };
};

type Props<T extends ReportSectionTableView> = {
  views: T[];
  activeKey: string;
  onSectionChange: (sectionType: string) => void;
  renderSection: (view: T, opts: { showSectionHeader: boolean }) => React.ReactNode;
};

/**
 * One card when there is a single section (no tablist). Two or more sections
 * use a tablist. Only the active tab is rendered. Active tab is URL-backed.
 */
export default function ReportSectionTables<T extends ReportSectionTableView>({
  views,
  activeKey,
  onSectionChange,
  renderSection,
}: Props<T>) {
  const ordered = orderReportSectionViews(views);
  if (ordered.length === 0) return null;

  if (ordered.length === 1) {
    return <>{renderSection(ordered[0], { showSectionHeader: true })}</>;
  }

  return (
    <Tabs
      activeKey={activeKey}
      onSelect={(key) => {
        if (key) onSectionChange(String(key));
      }}
      className="mb-3 ntcog-report-section-tabs"
    >
      {ordered.map((view) =>
        React.cloneElement(
          <Tab eventKey={view.section.type} title={view.section.label}>
            {view.section.type === activeKey
              ? renderSection(view, { showSectionHeader: false })
              : null}
          </Tab>,
          { key: view.section.type }
        )
      )}
    </Tabs>
  );
}
